import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../layouts';
import { PhoneInput } from '../components/form';
import { formatPhoneNumberForAPI } from '../utils/formatters';

const ResetPasswordPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get phone number and dial code from location state
  const initialPhoneNumber = location.state?.phoneNumber || '';
  const initialDialCode = location.state?.dialCode || '+61';

  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [dialCode, setDialCode] = useState(initialDialCode);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({
    phoneNumber: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { resetPassword } = useAuth();
  
  // Calculate password strength score
  const getPasswordStrength = (password: string): number => {
    let score = 0;
    if (password.length >= 8) score += 20;
    if (/[A-Z]/.test(password)) score += 20;
    if (/[a-z]/.test(password)) score += 20;
    if (/[0-9]/.test(password)) score += 20;
    if (/[^A-Za-z0-9]/.test(password)) score += 20;
    return score;
  };

  // Get password strength colour and label
  const getPasswordStrengthInfo = (score: number) => {
    if (score <= 20) return { color: 'bg-red-500', label: 'Very Weak', textColor: 'text-red-600' };
    if (score <= 40) return { color: 'bg-orange-500', label: 'Weak', textColor: 'text-orange-600' };
    if (score <= 60) return { color: 'bg-yellow-500', label: 'Fair', textColor: 'text-yellow-600' };
    if (score <= 80) return { color: 'bg-blue-500', label: 'Good', textColor: 'text-blue-600' };
    return { color: 'bg-green-500', label: 'Strong', textColor: 'text-green-600' };
  };

  // Validation functions
  const validateVerificationCode = (value: string): string => {
    if (!value) return 'Verification code is required';
    if (!/^\d+$/.test(value)) return 'Verification code must contain only numbers';
    return '';
  };
  
  const validatePassword = (value: string): string => {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(value)) return 'Password must contain at least one number';
    if (!/[^A-Za-z0-9]/.test(value)) return 'Password must contain at least one special character';
    return '';
  };
  
  const validateConfirmPassword = (value: string): string => {
    if (!value) return 'Please confirm your password';
    if (value !== newPassword) return 'Passwords do not match';
    return '';
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!phoneNumber || !verificationCode || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Password strength validation
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number');
      return;
    }
    
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setError('Password must contain at least one special character');
      return;
    }
    
    // Format phone number for API
    const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber, dialCode);
    
    try {
      setError('');
      setIsSubmitting(true);
      
      // Call resetPassword function from AuthContext
      const success = await resetPassword(formattedPhoneNumber, verificationCode, newPassword);
      
      if (success) {
        // Redirect to login page after successful password reset
        navigate('/login', { 
          state: { 
            message: 'Password reset successfully. You can now login with your new password.' 
          } 
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <MainLayout>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Reset Password</h1>
        
        {error && (
          <div className="bg-racing-red-light text-racing-red p-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="phoneNumber" className="block text-gray-700 mb-2">
              Phone Number
            </label>
            <PhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              onCountryCodeChange={setDialCode}
              defaultDialCode={initialDialCode}
              error={errors.phoneNumber}
              setError={(error) => setErrors(prev => ({ ...prev, phoneNumber: error }))}
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="verificationCode" className="block text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              id="verificationCode"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              onBlur={() => {
                const error = validateVerificationCode(verificationCode);
                setErrors(prev => ({ ...prev, verificationCode: error }));
              }}
              className={`w-full px-3 py-2 border ${errors.verificationCode ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-2 focus:ring-racing-blue`}
              placeholder="123456"
              required
            />
            {errors.verificationCode ? (
              <p className="text-red-500 text-xs mt-1">{errors.verificationCode}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Enter the verification code sent to your phone
              </p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="newPassword" className="block text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onBlur={() => {
                  const error = validatePassword(newPassword);
                  setErrors(prev => ({ ...prev, newPassword: error }));

                  // Also validate confirm password if it's not empty
                  if (confirmPassword) {
                    const confirmError = validateConfirmPassword(confirmPassword);
                    setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
                  }
                }}
                className={`w-full pr-10 px-3 py-2 border ${errors.newPassword ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-2 focus:ring-racing-blue`}
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(v => !v)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                {showNewPassword ? (
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
            {newPassword && (
              <div className="mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600">Password Strength</span>
                  <span className={`text-xs font-medium ${getPasswordStrengthInfo(getPasswordStrength(newPassword)).textColor}`}>
                    {getPasswordStrengthInfo(getPasswordStrength(newPassword)).label}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthInfo(getPasswordStrength(newPassword)).color}`}
                    style={{ width: `${getPasswordStrength(newPassword)}%` }}
                  ></div>
                </div>
              </div>
            )}
            {errors.newPassword ? (
              <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.
              </p>
            )}
          </div>
          
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => {
                  const error = validateConfirmPassword(confirmPassword);
                  setErrors(prev => ({ ...prev, confirmPassword: error }));
                }}
                className={`w-full pr-10 px-3 py-2 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-2 focus:ring-racing-blue`}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(v => !v)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-racing-blue text-white py-2 px-4 rounded hover:bg-racing-blue-dark transition ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Remember your password?{' '}
            <Link to="/login" className="text-racing-blue hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ResetPasswordPage;
